/**
 * Encodes a string for safe use in CSS context
 * 
 * This prevents XSS when embedding user data in:
 * - Inline styles
 * - CSS property values
 * - CSS selectors
 * @example
 * const userInput = "red; background: url('javascript:alert(1)')";
 */
export const encodeCss = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }

  const str = String(value);

  return str
    .replace(/\\/g, '\\\\')   // Backslash
    .replace(/"/g, '\\22 ')   // Double quote
    .replace(/'/g, '\\27 ')   // Single quote
    .replace(/</g, '\\3C ')   // Less than
    .replace(/>/g, '\\3E ')   // Greater than
    .replace(/&/g, '\\26 ')   // Ampersand
    .replace(/\(/g, '\\28 ')  // Open paren
    .replace(/\)/g, '\\29 ')  // Close paren
    .replace(/;/g, '\\3B ')   // Semicolon
    .replace(/:/g, '\\3A ')   // Colon
    .replace(/{/g, '\\7B ')   // Open brace
    .replace(/}/g, '\\7D ')   // Close brace
    .replace(/\n/g, '\\A ')   // Newline
    .replace(/\r/g, '\\D ')   // Carriage return
    .replace(/\t/g, '\\9 ')   // Tab
    .replace(/\0/g, '\\0 ');  // Null byte
};

/**
 * Validates a CSS color value
 * 
 * @param color - The color value to validate
 * @returns Validated color or empty string if invalid
 * 
 */
export const validateCssColor = (color: string | null | undefined): string => {
  if (!color) {
    return '';
  }

  const colorStr = String(color).trim();

  if (/^#[0-9A-Fa-f]{3,8}$/.test(colorStr)) {
    return colorStr;
  }

  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/.test(colorStr)) {
    return colorStr;
  }

  // Allow named colors (common ones)
  const namedColors = [
    'black', 'white', 'red', 'green', 'blue', 'yellow', 'cyan', 'magenta',
    'gray', 'grey', 'orange', 'purple', 'pink', 'brown', 'navy', 'teal',
    'olive', 'lime', 'aqua', 'maroon', 'silver', 'fuchsia', 'transparent',
  ];

  if (namedColors.includes(colorStr.toLowerCase())) {
    return colorStr;
  }

  // Block anything else (might contain injection)
  return '';
};

/**
 * Validates a CSS size value (with units)
 * 
 * @param size - The size value to validate
 * @returns Validated size or empty string if invalid
 */
export const validateCssSize = (size: string | null | undefined): string => {
  if (!size) {
    return '';
  }

  const sizeStr = String(size).trim();

  const validUnits = ['px', 'em', 'rem', '%', 'vh', 'vw', 'pt', 'cm', 'mm', 'in'];
  const pattern = new RegExp(`^\\d+(\\.\\d+)?(${validUnits.join('|')})$`);

  if (pattern.test(sizeStr) || sizeStr === '0') {
    return sizeStr;
  }

  return '';
};

